import { useId } from 'react'

import type { Capacidad, CatalogoDeCapacidades, Preset } from '../api/capacidades'
import { Casilla, Selector, clases } from '../componentes/ui'

/**
 * Qué puede hacer una persona en una cuenta, para elegirlo: el **preset** (el punto de
 * partida) y una casilla por capacidad, agrupadas como las agrupa el núcleo. Cambiar el
 * preset marca sus capacidades; con «propietario» van todas, marcadas y sin tocar: el
 * núcleo las pone igual (`accesos`), y una casilla que se puede desmarcar y no hace nada
 * engaña.
 *
 * El catálogo, las etiquetas y los presets vienen de `GET /api/panel/capacidades`: el panel
 * no lleva copia (§24). Lo usan la invitación (`PermisosDeInvitacion`) y la ficha de cada
 * persona (`AccesoDelUsuario`).
 */
export type Eleccion = { preset: Preset; capacidades: Capacidad[] }

/** Las capacidades de un preset según el catálogo; un preset que el catálogo no conoce, ninguna. */
export const capacidadesDelPreset = (catalogo: Pick<CatalogoDeCapacidades, 'presets'>, preset: string): Capacidad[] =>
  catalogo.presets.find((p) => p.value === preset)?.capacidades ?? []

/** El catálogo por grupos, en el orden de `grupos`; un grupo sin capacidades no sale. */
export const porGrupo = (catalogo: Pick<CatalogoDeCapacidades, 'capacidades' | 'grupos'>): Array<{ grupo: string; capacidades: CatalogoDeCapacidades['capacidades'] }> =>
  catalogo.grupos.map((grupo) => ({ grupo, capacidades: catalogo.capacidades.filter((c) => c.grupo === grupo) })).filter((g) => g.capacidades.length > 0)

/** Marcar o desmarcar una capacidad, sin repetir ninguna. */
export const alternar = (capacidades: Capacidad[], capacidad: Capacidad, marcada: boolean): Capacidad[] =>
  marcada ? [...new Set([...capacidades, capacidad])] : capacidades.filter((c) => c !== capacidad)

export const CasillasDeCapacidades = ({
  catalogo,
  eleccion,
  cambiar,
  soloLectura = false,
  presetsDisponibles,
}: {
  catalogo: CatalogoDeCapacidades
  eleccion: Eleccion
  cambiar: (eleccion: Eleccion) => void
  soloLectura?: boolean
  /** Qué presets se ofrecen; por omisión, todos los del catálogo. */
  presetsDisponibles?: Preset[]
}) => {
  const id = useId()
  const esPropietario = eleccion.preset === 'propietario'
  const marcadas = new Set<Capacidad>(esPropietario ? catalogo.capacidades.map((c) => c.value) : eleccion.capacidades)
  const presets = catalogo.presets.filter((p) => !presetsDisponibles || presetsDisponibles.includes(p.value) || p.value === eleccion.preset)

  return (
    <div className="flex flex-col gap-4">
      <label className="flex flex-col gap-1.5">
        <span className="text-[13px] font-medium text-tinta">Punto de partida</span>
        <Selector disabled={soloLectura} id={`${id}-preset`} onChange={(e) => cambiar({ preset: e.target.value as Preset, capacidades: capacidadesDelPreset(catalogo, e.target.value) })} value={eleccion.preset}>
          {presets.map((p) => (
            <option key={p.value} value={p.value}>
              {p.etiqueta}
            </option>
          ))}
        </Selector>
        <span className="text-xs text-tenue">
          {esPropietario ? 'El propietario puede hacerlo todo en la cuenta: las casillas van marcadas y no se recortan.' : 'Elegirlo marca sus casillas; después se afinan una a una.'}
        </span>
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        {porGrupo(catalogo).map(({ grupo, capacidades }) => (
          <fieldset className="flex min-w-0 flex-col gap-1.5" key={grupo}>
            <legend className="mb-1.5 text-[11px] font-semibold tracking-wider text-tenue uppercase">{grupo}</legend>
            {capacidades.map((c) => {
              const marcada = marcadas.has(c.value)
              const bloqueada = soloLectura || esPropietario
              return (
                <label className={clases('flex items-start gap-2 text-[13px]', bloqueada ? 'text-tinta-2' : 'cursor-pointer text-tinta')} key={c.value}>
                  <Casilla className="mt-0.5" disabled={bloqueada} marcada={marcada} onChange={(e) => cambiar({ preset: eleccion.preset, capacidades: alternar(eleccion.capacidades, c.value, e.target.checked) })} />
                  <span>{c.label}</span>
                </label>
              )
            })}
          </fieldset>
        ))}
      </div>
    </div>
  )
}
